import { Plus, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const REGION_OPTIONS = [
  { value: "", label: "All regions (no region scope)" },
  { value: "UAE", label: "UAE only" },
  { value: "Oman", label: "Oman only" },
  { value: "KSA", label: "KSA only" },
];

export default function CreateUserDialog({
  open,
  onOpenChange,
  darkMode,
  newUser,
  setNewUser,
  brands,
  creating,
  handleCreateUser,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-[#DC143C] hover:bg-[#b91031]" data-testid="create-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create User
        </Button>
      </DialogTrigger>
      <DialogContent className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
        <form onSubmit={handleCreateUser}>
          <DialogHeader>
            <DialogTitle className={darkMode ? "text-white" : ""}>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user, optionally scoped to a brand+region combination
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={darkMode ? "text-gray-300" : ""}>Name</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required
                placeholder="John Doe"
                className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                data-testid="new-user-name"
              />
            </div>

            <div className="space-y-2">
              <Label className={darkMode ? "text-gray-300" : ""}>Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                placeholder="john@example.com"
                className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                data-testid="new-user-email"
              />
            </div>

            <div className="space-y-2">
              <Label className={darkMode ? "text-gray-300" : ""}>Password</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                minLength={6}
                placeholder="••••••••"
                className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                data-testid="new-user-password"
              />
            </div>

            <div className="space-y-2">
              <Label className={darkMode ? "text-gray-300" : ""}>Role</Label>
              <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                <SelectTrigger
                  className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                  data-testid="new-user-role"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={darkMode ? "bg-[#21262d] border-[#30363d]" : ""}>
                  <SelectItem value="business" className={darkMode ? "text-white" : ""}>
                    Business User
                  </SelectItem>
                  <SelectItem value="admin" className={darkMode ? "text-white" : ""}>
                    Admin
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className={darkMode ? "text-gray-300" : ""}>Assigned Brand</Label>
              <Select value={newUser.brand} onValueChange={(val) => setNewUser({ ...newUser, brand: val })}>
                <SelectTrigger
                  className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                  data-testid="new-user-brand"
                >
                  <SelectValue placeholder="Select brand (required for business users)" />
                </SelectTrigger>
                <SelectContent className={darkMode ? "bg-[#21262d] border-[#30363d]" : ""}>
                  {brands.map((b) => (
                    <SelectItem key={b.value} value={b.value} className={darkMode ? "text-white" : ""}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {newUser.role === "admin"
                  ? "Admins can access all brands"
                  : "Business users can only access their assigned brand"}
              </p>
            </div>

            <div className="space-y-2">
              <Label className={darkMode ? "text-gray-300" : ""}>Region Scope (optional)</Label>
              <Select value={newUser.region || ""} onValueChange={(val) => setNewUser({ ...newUser, region: val })}>
                <SelectTrigger
                  className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                  data-testid="new-user-region"
                >
                  <SelectValue placeholder="No region restriction" />
                </SelectTrigger>
                <SelectContent className={darkMode ? "bg-[#21262d] border-[#30363d]" : ""}>
                  {REGION_OPTIONS.map((r) => (
                    <SelectItem
                      key={r.value || "__all__"}
                      value={r.value || "__all__"}
                      className={darkMode ? "text-white" : ""}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Lock this user to a specific region within their brand
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className={darkMode ? "border-[#30363d] text-gray-300" : ""}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating}
              className="bg-[#DC143C] hover:bg-[#b91031]"
              data-testid="submit-create-user"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
